import type {
  TransitionPresentation,
  TransitionPresentationComponentProps,
} from "@remotion/transitions";
import type React from "react";
import { AbsoluteFill, Easing, interpolate } from "remotion";
import { directionalTransforms, StackedBlur } from "./stacked-blur.tsx";
import { isTransitionIdle } from "./transition-idle.ts";
import { useStackedBlur, useTransitionBlur } from "./use-transition-blur.ts";

const clampOpts = {
  extrapolateLeft: "clamp" as const,
  extrapolateRight: "clamp" as const,
};

const STREAK_COPIES = 8;
const STREAK_DISTANCE = 10;
const STREAK_GAUSSIAN = 0.4;

export type WhipPanProps = {
  direction?: "left" | "right" | "up" | "down";
  blur?: number;
};

const WhipPanPresentation: React.FC<
  TransitionPresentationComponentProps<WhipPanProps>
> = ({
  children,
  presentationProgress,
  presentationDirection,
  passedProps,
}) => {
  const { direction = "left", blur: fullBlur = 24 } = passedProps;
  const blur = useTransitionBlur(fullBlur);
  const streaks = useStackedBlur();
  const entering = presentationDirection === "entering";
  const p = presentationProgress;

  if (isTransitionIdle(presentationDirection, p)) {
    return <AbsoluteFill>{children}</AbsoluteFill>;
  }

  const travel = interpolate(p, [0, 1], [0, 1], {
    ...clampOpts,
    easing: Easing.bezier(0.7, 0, 0.2, 1),
  });
  const velocity = Math.sin(Math.PI * travel);

  const axis: "x" | "y" =
    direction === "left" || direction === "right" ? "x" : "y";
  const sign = direction === "left" || direction === "up" ? -1 : 1;

  const offset = entering ? (travel - 1) * 100 * sign : travel * 100 * sign;
  const stretch = 1 + velocity * 0.08;
  const overscan = 1 + velocity * 0.045;

  const translate = axis === "x" ? `${offset}%` : `0 ${offset}%`;
  const smear =
    axis === "x" ? `${stretch} ${overscan}` : `${overscan} ${stretch}`;

  const blurPx = velocity * blur * (streaks ? STREAK_GAUSSIAN : 1);

  const style: React.CSSProperties = {
    translate,
    scale: smear,
    filter: blurPx >= 0.05 ? `blur(${blurPx}px)` : undefined,
  };

  return (
    <AbsoluteFill style={style}>
      {streaks && blurPx >= 0.05 ? (
        <StackedBlur
          transforms={directionalTransforms(
            axis,
            -sign * STREAK_DISTANCE * velocity,
            STREAK_COPIES,
          )}
        >
          {children}
        </StackedBlur>
      ) : (
        children
      )}
    </AbsoluteFill>
  );
};

export function whipPan(
  props: WhipPanProps = {},
): TransitionPresentation<WhipPanProps> {
  return {
    component: WhipPanPresentation,
    props,
  };
}
